import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import Reveal from '@/components/generals/reveal'
import { FINISHES, SPEC_GROUPS } from '@/constants/landing'

const Specs: FC = (): ReactNode => {
    return (
        <section id='specs' aria-labelledby='specs-title' className='bg-ink text-ink-foreground'>
            <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-28'>
                <div className='grid gap-8 lg:grid-cols-12 lg:gap-10'>
                    <Reveal className='lg:col-span-4'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                            Specifications
                        </p>

                        <h2 id='specs-title' className='mt-5 font-heading text-[clamp(2.6rem,5vw,4.5rem)] leading-[0.96] tracking-[-0.02em]'>
                            The details, <em className='italic text-ember'>plainly.</em>
                        </h2>

                        <p className='mt-6 max-w-sm text-lg leading-relaxed text-ink-foreground/70'>
                            Targets for the first production units, measured in lived-in homes rather than a lab.
                        </p>

                        <div className='mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-6'>
                            <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ink-foreground/60'>
                                Knit finishes
                            </p>

                            <ul className='mt-5 flex flex-wrap gap-5'>
                                {FINISHES.map(finish => (
                                    <li key={finish.name} className='flex items-center gap-3'>
                                        <span className={cn('size-9 rounded-full ring-1 ring-white/20 ring-offset-2 ring-offset-ink', finish.swatch)} aria-hidden='true' />

                                        <span className='text-[15px]'>
                                            {finish.name}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <p className='mt-5 text-sm leading-relaxed text-ink-foreground/60'>
                                Covers unzip and go in a normal wash. Swap colours whenever you redecorate.
                            </p>
                        </div>
                    </Reveal>

                    <div className='grid gap-px overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 sm:grid-cols-2 lg:col-span-8 xl:grid-cols-3'>
                        {SPEC_GROUPS.map((group, index) => (
                            <Reveal key={group.title} delay={index * 60} className='bg-ink p-6 md:p-7'>
                                <h3 className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                                    {group.title}
                                </h3>

                                <dl className='mt-5 flex flex-col'>
                                    {group.rows.map(row => (
                                        <div key={row.label} className='border-t border-white/10 py-3 first:border-t-0 first:pt-0'>
                                            <dt className='text-[13px] text-ink-foreground/55'>
                                                {row.label}
                                            </dt>

                                            <dd className='mt-1 text-[15px] leading-snug text-ink-foreground'>
                                                {row.value}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <p className='mt-8 text-sm text-ink-foreground/50'>
                    Specifications describe first production units and may change before delivery. Runtime depends on the tasks and the home.
                </p>
            </div>
        </section>
    )
}

export default Specs
